/* json_editor.dom.js */

/* global ace */
/**
 * @module json_editor.dom
 *
 * Framework-agnostic factory that wraps Ace with the same look/feel as
 * gl_json_editor's AngularJS directive (CME's JSON pane), plus the
 * schema-validation + inline gutter-marker wiring used by the audit policy
 * editor's JSON mode.
 *
 * Use this from non-Angular hosts (e.g. game_center components that build
 * raw DOM). The resulting object exposes a small API:
 *
 *   create({container, schemas, extraValidators, onValidationChange,
 *           onUserChangedContent, readOnly, initialContent}) → {
 *     setContent(value), getContent(), getValue(),
 *     setSchemas(entries), setExtraValidators(fns),
 *     setReadOnly(b), runValidation(),
 *     focus(), destroy()
 *   }
 *
 * `extraValidators` are functions `(parsedValue, editorText) → errors[]`
 * that run after Ajv validation when the JSON is syntactically valid.
 * Their errors merge into the gutter / annotation pipeline alongside
 * the schema errors — used for domain checks that aren't expressible
 * as a static JSON schema.
 *
 * `schemas` matches json_schema_validator's expected shape
 * ({schema, pointer?, label?}) and is also configurable post-create via
 * setSchemas(). `onValidationChange` fires with the normalized errors array
 * after every parse/validation pass.
 */
const _ = require("lodash");
const SchemaValidator = require("./utils/json_schema_validator");
const AceAnnotations = require("./utils/ace_annotations");

const ACE_BASE_PATH = "/resources/ace-builds/src-min-noconflict";
const EMPTY_JSON_PLACEHOLDER = "{\n\t\n}";

const create = (createOptions) => {
    const {
        container: hostContainer,
        onValidationChange,
        onUserChangedContent,
        readOnly: isReadOnly = false,
        initialContent,
    } = createOptions || {};
    if (!hostContainer) {
        throw new Error("json_editor.dom.create: container is required");
    }

    const editorElement = document.createElement("div");
    editorElement.className = "json_editor";
    editorElement.style.width = "100%";
    editorElement.style.height = "100%";
    hostContainer.appendChild(editorElement);

    const aceEditor = ace.edit(editorElement);
    aceEditor.setShowPrintMargin(false);
    const editorSession = aceEditor.getSession();

    let aceBasePath = ACE_BASE_PATH;
    if (window.location.pathname !== "/") {
        aceBasePath = window.location.pathname + aceBasePath;
    }
    ace.config.set("basePath", aceBasePath);

    aceEditor.setShowFoldWidgets(true);
    aceEditor.setTheme("ace/theme/chrome");
    aceEditor.setFadeFoldWidgets(true);
    aceEditor.$blockScrolling = Infinity;
    editorSession.setMode("ace/mode/json");
    editorSession.setFoldStyle("markbegin");
    // Ace's JSON mode ships with a worker that runs its own parser on every
    // edit and calls session.setAnnotations(...) with the results. Because
    // setAnnotations is a *full replacement* (not a merge), the worker
    // wipes out any annotations we just installed — including schema /
    // enum / customField errors — a beat after they're drawn. We surface
    // syntax errors ourselves via SchemaValidator.parseJson, so the
    // worker's output is redundant. Turn it off so the gutter shows only
    // *our* annotations and they actually stick.
    editorSession.setUseWorker(false);
    aceEditor.renderer.setShowGutter(true);
    aceEditor.setReadOnly(!!isReadOnly);

    let schemaEntries = (createOptions && createOptions.schemas) || [];
    // Optional consumer-supplied validators that run AFTER the Ajv pass.
    // Each takes `(parsedValue, editorText)` and returns an array of
    // `{line, column, message, jsonPointer}` errors. Used by audit policy's
    // JSON pane to layer in domain checks (e.g. custom-field enum values)
    // that aren't expressible as a plain JSON Schema.
    let extraValidators = (createOptions && createOptions.extraValidators) || [];
    let savedContent;
    // Marker IDs from the most recent validation pass. Kept in closure so
    // we don't mutate hidden state on the Ace session.
    let activeMarkerIds = [];

    const serializeToPrettyJson = (value) => {
        if (value === null || value === undefined) {
            return EMPTY_JSON_PLACEHOLDER;
        }
        if (_.isString(value)) {
            // Pretty-print if it parses; otherwise pass through.
            try {
                return JSON.stringify(JSON.parse(value), null, "\t");
            } catch (parseError) {
                return value;
            }
        }
        return JSON.stringify(value, null, "\t");
    };

    const runValidation = () => {
        const editorText = aceEditor.getValue();
        const parseResult = SchemaValidator.parseJson(editorText);
        let normalizedErrors;
        if (!parseResult.ok) {
            normalizedErrors = parseResult.errors;
        } else {
            normalizedErrors = SchemaValidator.validate(
                editorText,
                parseResult.value,
                schemaEntries
            );
            // Run consumer-supplied extra validators only when JSON is
            // syntactically valid — they assume a parsed structure to walk.
            _.each(extraValidators, (validator) => {
                try {
                    const extraErrors = validator(parseResult.value, editorText) || [];
                    if (extraErrors.length) {
                        normalizedErrors = normalizedErrors.concat(extraErrors);
                    }
                } catch (validatorError) {
                    // Don't let a broken consumer validator hide schema
                    // errors — log and carry on.
                    if (typeof console !== "undefined" && console.warn) {
                        console.warn("json_editor extra validator threw", validatorError);
                    }
                }
            });
        }
        activeMarkerIds = AceAnnotations.applyToSession(
            editorSession,
            normalizedErrors,
            activeMarkerIds
        );
        const parsedValue = parseResult.ok ? parseResult.value : undefined;
        if (onValidationChange) {
            onValidationChange({
                errors: normalizedErrors,
                valid: !normalizedErrors.length,
                parsed: parsedValue,
            });
        }
        return {errors: normalizedErrors, parsed: parsedValue};
    };

    const setContent = (value) => {
        const prettyText = serializeToPrettyJson(value);
        aceEditor.setValue(prettyText, -1);
        aceEditor.gotoLine(1);
        aceEditor.scrollToLine(1);
        try {
            savedContent = _.isString(value) ? JSON.parse(value) : value;
        } catch (parseError) {
            savedContent = value;
        }
        runValidation();
    };

    const getContent = () => savedContent;

    const getValue = () => {
        const editorText = aceEditor.getValue();
        try {
            return JSON.parse(editorText);
        } catch (parseError) {
            return editorText;
        }
    };

    const setSchemas = (newSchemaEntries, options) => {
        schemaEntries = _.isArray(newSchemaEntries) ? newSchemaEntries : [];
        if (!options || !options.skipValidation) {
            runValidation();
        }
    };

    const setExtraValidators = (newValidators, options) => {
        extraValidators = _.isArray(newValidators) ? newValidators : [];
        if (!options || !options.skipValidation) {
            runValidation();
        }
    };

    const setReadOnly = (readOnly) => aceEditor.setReadOnly(!!readOnly);

    // Debounced so rapid keystrokes coalesce. Trailing-only because the user
    // expects gutter markers to settle after they stop typing, not before.
    const runValidationDebounced = _.debounce(runValidation, 100, {trailing: true});

    aceEditor.on("change", (changeEvent) => {
        if (!aceEditor.curOp || !aceEditor.curOp.command || !aceEditor.curOp.command.name) {
            return;
        }
        runValidationDebounced();
        if (onUserChangedContent) {
            try {
                onUserChangedContent({changeEvent, newValue: getValue()});
            } catch (handlerError) {
                /* ignore — handler errors are not our problem */
            }
        }
    });
    aceEditor.on("focus", () => aceEditor.resize());

    if (initialContent !== undefined) {
        setContent(initialContent);
    } else {
        aceEditor.setValue(EMPTY_JSON_PLACEHOLDER, -1);
        aceEditor.gotoLine(2);
        aceEditor.scrollToLine(2);
    }

    return {
        setContent,
        getContent,
        getValue,
        setSchemas,
        setExtraValidators,
        setReadOnly,
        runValidation,
        focus: () => aceEditor.focus(),
        resize: () => aceEditor.resize(true),
        destroy: () => {
            runValidationDebounced.cancel();
            AceAnnotations.clearSession(editorSession, activeMarkerIds);
            activeMarkerIds = [];
            aceEditor.destroy();
            if (editorElement.parentNode) {
                editorElement.parentNode.removeChild(editorElement);
            }
        },
    };
};

module.exports = {create};
